import {
  CertificationIndexResponse,
  CreateApiParam,
  SignatureGroupType,
} from '@stormwater-software/lib-certification';
import xhrService from '../xhrService';
import db from '../../db/db';
import { baseUrl as baseInspectionUrl } from './inspection';
import { getDateOnly } from '../date';
import { AxiosPromise, AxiosResponse } from 'axios';
import { withOfflineFallback, withOfflineWrite } from '../offlineFallback';
import { syncAfterOnlineSave } from '../offlineSyncSend';
import {
  ensureOwnDownload,
  markQueuedSignatures,
  offlineSignBlock,
  projectToWaitFor,
  withQueuedSignatures,
} from '../offlineSigning';
import dayjs from 'dayjs';

export type BulkCertifyParam = { id: number; date: string; lineType: string }[];

/**
 * SW2-2248 — mirrors the backend's own `CertStateReview`
 * (`inspectionCertificationService.ts`) field-for-field. Widened locally rather than in
 * `@stormwater-software/lib-certification` for the same reason the backend keeps its copy local
 * too: `CertState`/`CertificationIndexResponse` live in that published package, and a library
 * release is not in scope for this ticket.
 */
export type CertStateReview = {
  /** The review row's own PK — needed to call `inspectionReviewApi.reject`/`cancel`. */
  id: number | null;
  /** `not_raised` is the normal pre-signing state: the form requires review, but nobody has
   * submitted anything for it to raise a row yet. */
  /** `released` (decision C): Inspection Review was turned off while this review was pending, and
   * the report went out without review. */
  status: 'not_raised' | 'pending' | 'approved' | 'rejected' | 'cancelled' | 'released';
  reviewerName: string | null;
  rejectionReason: string | null;
  submittedAt: string | null;
  respondedAt: string | null;
  /** Whether the CURRENT viewer is the resolved reviewer and may sign/reject. */
  canSign: boolean;
  /** Whether the CURRENT viewer is this review's own submitter and may withdraw it. */
  canCancel: boolean;
  showSignature: boolean;
};

export type CertificationIndexResponseWithReview = CertificationIndexResponse & {
  review: CertStateReview | null;
};

/**
 * One transaction: the row is read, added to and written back whole, so a sync taking a sent
 * signature off it in between (`takeSentEntries`) was undone — the sent entry came back and was
 * sent again, and the new one stuck behind its refusal (PR #156 review).
 */
const createOfflineCertification = (
  inspectionId: number,
  group: string,
  data: any,
  resend?: boolean,
  online?: boolean,
) =>
  db.transaction('rw', db.certifications, db.inspections, db.findings, db.user, () =>
    writeOfflineCertification(inspectionId, group, data, resend, online),
  );

async function writeOfflineCertification(
  inspectionId: number,
  group: string,
  data: any,
  resend?: boolean,
  online?: boolean,
) {
  // SW2-2459: fails loudly. It used to resolve quietly when the row was missing or a read or
  // write failed, so a signature could be reported "Signed" with nothing stored or queued.
  const existingCertification = await db.certifications.get(inspectionId);

  if (!existingCertification) {
    // Online, the server has the signature: there is simply nothing on this device to mirror.
    if (online) return;

    throw new Error('This inspection is not stored on this device, so the signature could not be saved here.');
  }

  const findings = await db.findings
    .where('inspection_id')
    .equals(inspectionId)
    .toArray()
    .catch((error: any) => {
      console.log('Error getting offline inspection findings', error);

      return Promise.reject(error);
    });

  const user = await db.user.get(1).catch((error: any) => {
    console.log('Error getting offline user', error);

    return Promise.reject(error);
  });

  if (!user) {
    throw new Error('No user is stored on this device.');
  }

  // SW2-2459: one queued signature per line. A second (the line looked unsigned because the device
  // row couldn't be read) was refused by the server as already submitted, and stayed queued ahead
  // of every later signature (PR #156 review). A Resend is a separate request, so it is allowed.
  if (
    !online &&
    !resend &&
    (existingCertification.sync?.payload || []).some(
      (p: any) => p.group === group && p.lineType === data.lineType && !p.resend,
    )
  ) {
    throw new Error('This line is already signed on this device and waiting to upload');
  }

  const certId = Math.floor(Math.random() * 100000);
  const certObj = {
    date: getDateOnly(data.date),
    email: user.email,
    imgSrc: '',
    lineType: data.lineType,
    signed_date: getDateOnly(new Date()),
    userName: user.name,
    userTitle: user.role,
    userId: user.user_id,
    id: certId,
  };

  // SW2-2446: an online signature is already on the server: keep any signature queued offline
  // before it, rather than clearing the queue and marking it synced.
  existingCertification.sync = online
    ? syncAfterOnlineSave(existingCertification.sync)
    : {
        ...existingCertification.sync,
        synced: false,
        payload: [
          ...(existingCertification.sync?.payload || []),
          {
            group: group,
            date: getDateOnly(data.date),
            lineType: data.lineType,
            emailMessage: data.emailMessage,
            default_message: data.default_message,
            resend: resend,
            id: certId,
          },
        ],
      };

  existingCertification.userSignatures[group].push(certObj);

  if (
    existingCertification.userSignatures[group].length ===
    existingCertification.signatureGroups[group].length
  ) {
    const field =
      group === SignatureGroupType.compliance
        ? 'compliance_date'
        : group === SignatureGroupType.acknowledgement
          ? 'acknowledgement_date'
          : 'certification_date';

    // SW2-2459: a queued signature that completes its group records what it changed on the
    // inspection, so a refusal can put it back — left, the inspection stayed complete and
    // read-only on the device after the server refused the signature (PR #156 review).
    if (!online) {
      const before: any = await db.inspections.get(inspectionId);
      const queue = existingCertification.sync.payload;

      queue[queue.length - 1].completes = {
        field,
        date: before?.[field] ?? null,
        status: group === SignatureGroupType.inspection ? before?.status ?? null : undefined,
      };
    }

    if (group === SignatureGroupType.inspection) {
      await db.inspections.update(inspectionId, { status: 'complete' });
    }

    if (
      group === SignatureGroupType.inspection &&
      existingCertification.signatureGroups['acknowledgement'].length
    ) {
      existingCertification.groupOptions['acknowledgement'].enabled = true;
      existingCertification.groupOptions['acknowledgement'].errors = [];
    }

    if (
      (group === SignatureGroupType.acknowledgement &&
        existingCertification.signatureGroups['compliance'].length) ||
      (group === SignatureGroupType.inspection &&
        existingCertification.signatureGroups['compliance'].length &&
        existingCertification.signatureGroups['acknowledgement'].length === 0)
    ) {
      // console.log('Checking compliance findings for completion');

      const requiredFindings = findings
        ? findings.filter((f: any) => f.is_required_for_cert)
        : [];
      const completedRequiredFinings = requiredFindings.filter(
        (f: any) => f.date_completed,
      );
      const requiredFindingsCompleted =
        completedRequiredFinings.length === requiredFindings.length;

      // console.log('Required findings completed:', requiredFindingsCompleted);
      // console.log('Required findings:', requiredFindings);
      // console.log('Completed required findings:', completedRequiredFinings);
      // console.log('Existing certification:', existingCertification);

      if (!requiredFindingsCompleted) {
        existingCertification.groupOptions['compliance'].enabled = false;
        existingCertification.groupOptions['compliance'].errors = [
          'Findings need to be completed prior to signing',
        ];
      } else {
        existingCertification.groupOptions['compliance'].enabled = true;
        existingCertification.groupOptions['compliance'].errors = [];
      }
    }

    await db.inspections.update(inspectionId, { [field]: getDateOnly(new Date()) });
  }

  await db.certifications.update(inspectionId, existingCertification);

  return certId;
}

/**
 * SW2-2239 follow-up. Send a calendar DAY, never an instant.
 *
 * `getCertLineDefaultDate` returns a `Date` at the browser's **local midnight**, and that return
 * type is load-bearing — `lib-form`'s `DateControl` hands it to react-datepicker as `selected`,
 * which renders in browser-local time. Local midnight is the only representation that displays as
 * the same calendar day in every zone.
 *
 * But serialising it puts an instant on the wire, and local midnight in any UTC-positive zone is
 * the PREVIOUS day in UTC: a signer in Asia/Kolkata (UTC+5:30) sends `2026-09-03T18:30:00.000Z`
 * for their 4 September. The server's `getCalendarDay` reads a bare instant in UTC, so the
 * submitted day resolved to 3 September, the floor was the 4th, and signing failed with
 * "cannot be dated before 09-04-2026". Every US zone is UTC-negative, so nobody in the US could
 * reproduce it.
 *
 * No server-side reading fixes this: that instant genuinely IS 3 September in UTC, in Denver and
 * in New York. The signer's intent is destroyed at serialisation, so it has to be preserved here.
 *
 * `MM-DD-YYYY` is chosen because it is already the wire shape the moment a signer touches the
 * picker (`DateControl` emits `format(val, "MM-dd-yyyy")`), and the server matches it literally
 * with no zone consulted. This makes the untouched pre-fill behave like an edited one rather than
 * taking a different, zone-sensitive path.
 *
 * Only the signer's asserted calendar day travels through here — `signee_date` and the
 * inspection-level date column. The real signing instant is `inspection_signatures.created_at`,
 * which the server generates and the client never sends.
 */
const DAY_SHAPES = [/^\d{2}-\d{2}-\d{4}$/, /^\d{4}-\d{2}-\d{2}$/];

export function toWireCalendarDay(value: unknown): unknown {
  if (value === null || value === undefined || value === '') return value;

  // Already a calendar day. Re-parsing would be actively harmful: with no format hint
  // `dayjs('09-04-2026')` falls back to `Date` parsing and does not round-trip.
  if (
    typeof value === 'string' &&
    DAY_SHAPES.some((re) => re.test(value.trim()))
  ) {
    return value.trim();
  }

  // A Date, or an ISO instant that came from one. Formatted in LOCAL time — the frame the value
  // was built in — so the calendar day the signer saw is the day that goes on the wire.
  const parsed = dayjs(value as string | Date);

  return parsed.isValid() ? parsed.format('MM-DD-YYYY') : value;
}

/**
 * SW2-2459 — checked again as the bulk sign is sent, not only when its modal checked: work queued
 * in another tab since, or a sync started there, isn't seen by the modal (PR #156 review).
 */
async function ensureNoOfflineWork(rows: BulkCertifyParam) {
  const reason = await offlineSignBlock(Array.from(new Set(rows.map((row) => row.id))));

  if (reason) throw new Error(reason);
}

/**
 * SW2-2248: what an online sign did about Inspection Complete. Both false when the signature did
 * not trigger distribution. Absent on an offline sign, which never reaches the server here.
 */
export interface CertificationCreateResponse {
  distributed: boolean;
  reviewRaised: boolean;
}

export const certificationApi = {
  /**
   * @todo argument signatureLineId: string
   * @todo new api `${baseInspectionUrl}/${inspectionId}/signature/${signatureLineId}`
   */
  create: async (
    { inspectionId, group, ...data }: CreateApiParam,
    resend?: boolean,
    // SW2-2321: `opts.replay` must be passed when this call is the flush of an
    // already-queued write, so an unreachable server rejects instead of
    // re-queueing a write the sync loop is about to mark as synced.
    opts: { replay?: boolean } = {},
  ) => {
    // SW2-2239 follow-up: a calendar day on the wire, not an instant. Applied before the
    // request and before the offline mirror, so both record the same day.
    if (data && typeof data === 'object' && 'date' in data) {
      (data as { date?: unknown }).date = toWireCalendarDay(
        (data as { date?: unknown }).date,
      );
    }

    // SW2-2459: on a downloaded project with offline work waiting (or a sync running), queue the
    // signature rather than send it — sent now, it would email a report without that work. The
    // sync sends it after the rest, and is started here. Not for a replay: that *is* the sync.
    if (!opts.replay) {
      const waitFor = await projectToWaitFor(inspectionId);

      if (waitFor) {
        // Without its signature lines on the device it can't be queued, and sent now the report
        // would go out without the work: signing again can't help until the work is uploaded
        // (PR #156 review).
        if (!(await db.certifications.get(Number(inspectionId)))) {
          throw new Error(
            "This project has offline work waiting to upload, and this inspection's signature lines aren't stored on this device. Sync the project from the dashboard, then sign",
          );
        }

        // Only "queued" when it really is: the transaction that wrote this signature's entry
        // returns its id. Read back afterwards, a sync already running could have sent it in
        // between, and a sent signature was reported as not saved (PR #156 review).
        const certId = await createOfflineCertification(inspectionId, group, data, resend, false);

        if (certId == null) {
          throw new Error('The signature could not be queued on this device.');
        }

        // Offline, nothing can be sent: the automatic sync starts when the device is back online
        // (starting it here only showed "Upload paused").
        if (navigator.onLine !== false) {
          import('../offlineSyncCoordinator')
            .then(({ sendQueuedWork }) => sendQueuedWork(waitFor))
            .catch((err) =>
              // Queued all the same: the next app start, reconnect or return to the app sends it.
              // eslint-disable-next-line no-console
              console.error(`Signature queued, but its upload could not be started now`, err),
            );
        }

        return { queued: true, projectId: waitFor } as any;
      }
    }

    // SW2-2321: was a bare `navigator.onLine` pre-check, which reports true after
    // an offline reload — so signing went to the network, failed, and never reached
    // createOfflineCertification. The signature was lost with no error and nothing
    // queued, on the one action the inspector believes completes the inspection.
    // The generic is the *resolved* value, not a promise type. `AxiosPromise<T>`
    // extends `Promise<AxiosResponse<T>>`, so passing it here made T a promise;
    // TypeScript then flattens the async request callback's return and the two
    // can never unify (TS2345). `tsc --noEmit` does not surface it in this repo
    // — only the production build's checker does.
    return withOfflineWrite<AxiosResponse<CertificationCreateResponse | void>>(
      async () => {
        const certData = await xhrService.post<CertificationCreateResponse>(
          `${baseInspectionUrl}/${inspectionId}/cert/${group}`,
          { data, resend },
        );

        // Mirror the applied write locally. Kept inside the request so behaviour is
        // unchanged if it throws, and safe to leave there: a Dexie failure is not
        // network-shaped, so withOfflineWrite rethrows rather than queueing a write
        // the server has already applied.
        //
        // SW2-2321: skipped on replay. A replay flushes a certification that is
        // already in IndexedDB, and this mirror is create-style: with
        // `online = true` it sets `sync.synced = true`, **clears the entire
        // remaining payload array**, and pushes another signature. So the first
        // successful replay destroyed every signature queued behind it, and no
        // all-payload guard in the caller could recover them. Replay is
        // wire-only; the sync loop reconciles the local row.
        // (Raised by Copilot on PR #53.)
        if (!opts.replay) {
          // SW2-2459: best-effort. The server has the signature; a device write failing here
          // (now that the mirror reports its failures) must not turn a saved signature into
          // "not saved" — and must never reach withOfflineWrite, which would queue it again.
          try {
            await createOfflineCertification(inspectionId, group, data, resend, true);
          } catch (err) {
            console.error('Signature saved, but not mirrored to this device', err);
          }
        }

        return certData;
      },
      async () => {
        await ensureOwnDownload(inspectionId);

        return createOfflineCertification(
          inspectionId,
          group,
          data,
          resend,
          false,
        ) as unknown as Promise<AxiosResponse<CertificationCreateResponse | void>>;
      },
      opts,
    );
  },
  index: async (inspectionId: number) =>
    // SW2-2321: converted from a `navigator.onLine` pre-check. After an offline
    // reload the check passed, the request failed, and the certifications tab was
    // empty even though the data was cached.
    withOfflineFallback<CertificationIndexResponseWithReview>(
      () =>
        xhrService
          .get<CertificationIndexResponseWithReview>(
            `/api/inspections/${inspectionId}/cert`,
          )
          // SW2-2459: with signatures queued on the device, show them too — until the sync sends
          // them the server doesn't have them, and the line would look unsigned.
          .then(async (res) =>
            withQueuedSignatures(
              res.data,
              await db.certifications.get(Number(inspectionId)).catch(() => undefined),
            ),
          ),
      // Offline, the device's row — its queued signatures marked as such too.
      () => db.certifications.get(Number(inspectionId)).then(markQueuedSignatures),
    ),

  bulkCertify: async (data: BulkCertifyParam) => {
    await ensureNoOfflineWork(data);

    return xhrService.post(
      `${baseInspectionUrl}/cert/${SignatureGroupType.compliance}`,
      // SW2-2239 follow-up: the bulk modals seed their rows from the same
      // `getCertLineDefaultDate`, so they carry the same local-midnight Date.
      data.map((row) => ({
        ...row,
        date: toWireCalendarDay(row.date) as string,
      })),
    );
  },

  bulkInspectionCertify: async (data: BulkCertifyParam) => {
    await ensureNoOfflineWork(data);

    return xhrService.post(
      `${baseInspectionUrl}/cert/${SignatureGroupType.inspection}`,
      // SW2-2239 follow-up: the bulk modals seed their rows from the same
      // `getCertLineDefaultDate`, so they carry the same local-midnight Date.
      data.map((row) => ({
        ...row,
        date: toWireCalendarDay(row.date) as string,
      })),
    );
  },

  bulkAcknowledgementCertify: async (data: BulkCertifyParam) => {
    await ensureNoOfflineWork(data);

    return xhrService.post(
      `${baseInspectionUrl}/cert/${SignatureGroupType.acknowledgement}`,
      // SW2-2239 follow-up: the bulk modals seed their rows from the same
      // `getCertLineDefaultDate`, so they carry the same local-midnight Date.
      data.map((row) => ({
        ...row,
        date: toWireCalendarDay(row.date) as string,
      })),
    );
  },

  getAllSignedCompliancesWithCount: (inspectionId: number) =>
    // SW2-2321: converted from a `navigator.onLine` pre-check. The offline branch
    // also read `certification.userSignatures` without checking that the record
    // exists; withOfflineFallback's cache read swallows that throw and falls
    // through to the request rather than rejecting the caller.
    withOfflineFallback<any>(
      () =>
        xhrService
          .get(
            `/api/inspections/${inspectionId}/cert/allSignedCompliancesWithCount`,
          )
          .then((res) => res.data),
      () =>
        db.certifications.get(inspectionId).then((certification: any) => {
          const signedCompliances = certification.userSignatures[
            SignatureGroupType.compliance
          ].filter((signature: any) => signature.date);
          const signatures = signedCompliances.map((signature: any) => {
            return {
              ccrNumber: signature.ccrNumber,
              peNumber: signature.peNumber,
              signee_date: signature.signed_date,
              signee_name: signature.userName,
              signee_title: signature.userTitle,
              type: signature.lineType,
              email: signature.email,
            };
          });

          return {
            rows: signatures,
            count: signedCompliances.length,
          };
        }),
    ),

  removeCertification: (inspectionId: number, data: any) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // POST was attempted, failed, and the local removal never ran — so the
    // signature the inspector had just removed reappeared.
    withOfflineWrite<any>(
      () =>
        xhrService.post(
          `/api/inspections/${inspectionId}/removeCertification`,
          data,
        ),
      () =>
        db.certifications
          .get(inspectionId)
          .then((certification: any) => {
            if (!certification) {
              return Promise.reject('Certification not found');
            }

            const group = data.signatureGroup;
            const certIds = data.certIds || [];
            const userSignatures = certification.userSignatures[group];
            const updatedSignatures = userSignatures.filter(
              (signature: any) => !certIds.includes(signature.id),
            );

            certification.userSignatures[group] = updatedSignatures;
            certification.sync.payload = certification.sync.payload.filter(
              (payload: any) => !certIds.includes(payload.id),
            );

            return db.certifications.put(certification).then(() => {
              return Promise.resolve() as unknown as AxiosPromise<any>;
            });
          })
          .catch((error: any) => {
            // Kept: a failure to queue is a real failure the caller must see.
            // withOfflineWrite only reaches here when the request was
            // network-shaped, so there is no server write to contradict.
            console.log(
              'Error removing offline inspection certification',
              error,
            );

            return Promise.reject(error);
          }),
      // SW2-2321: `localOnlyWhenOffline` because nothing replays this local
      // write — the sync loop handles only create/update payloads. Resolving on
      // an unreachable server would claim the change will sync when it never
      // will, and the next download would restore the server's version.
      // (Raised by Copilot on PR #53.)
      { localOnlyWhenOffline: true },
    ),
};
